import { DateInputBlock } from '@coast/schemas';
import { isNil } from 'lodash';
import { useCallback, useState } from 'react';
import { Keyboard, TouchableOpacity } from 'react-native';
import { View } from '@shared/components/View';
import { useCardFormContext } from '@shared/components/blocks/contexts/form';
import { Range } from '@shared/components/dateAndTime/comboDatePicker/props';
import { DateString } from '@shared/scenes/cards/agenda/utils';
import { getAnchorName } from '@shared/styles';
import { renderClearButton } from '../input';
import { MBCardInputBlockBase } from '../input/base';
import { NoCodeInputBlockDate } from './NoCodeInputBlockDate';
import { MBCardInputBlockDateContent } from './content';
import { MBCardInputBlockDatePicker } from './picker';
import { PickerMode } from './types';

interface Props {
  block: DateInputBlock;
  workflowTemplateId?: string;
}

export function CardInputBlockDate({ workflowTemplateId, block }: Props) {
  if (isNil(workflowTemplateId)) {
    return <MBCardInputBlockDate block={block} />;
  } else {
    return <NoCodeInputBlockDate block={block} workflowTemplateId={workflowTemplateId} />;
  }
}

export function MBCardInputBlockDate({ block }: Props) {
  const { values, setFieldValue } = useCardFormContext();
  const value = values[block.name];
  const anchorName = getAnchorName('input-block-date', block.name);

  const [pickerMode, setPickerMode] = useState<PickerMode>();

  const handleOpenPicker = useCallback((mode: PickerMode) => {
    setPickerMode(mode);
    Keyboard?.dismiss();
  }, []);
  const handleClosePicker = useCallback(() => setPickerMode(undefined), []);
  const handlePress = useCallback(() => {
    if (block.fieldType === 'time') {
      handleOpenPicker(PickerMode.Time);
    } else {
      handleOpenPicker(PickerMode.Date);
    }
  }, [handleOpenPicker, block.fieldType]);

  const onChange = useCallback(
    (updatedValue: Range<DateString>) => setFieldValue(block.name, updatedValue.start),
    [block.name, setFieldValue],
  );

  const renderRight = useCallback(
    (inputBlock: DateInputBlock) => {
      if (!value) {
        return <View />;
      }
      if (!!inputBlock.label) {
        return <View paddingTop={2}>{renderClearButton(inputBlock, handleClosePicker)}</View>;
      }
      return renderClearButton(inputBlock);
    },
    [value, handleClosePicker],
  );

  return (
    <>
      <TouchableOpacity onPress={!!pickerMode ? handleClosePicker : handlePress}>
        <View style={{ anchorName }}>
          <MBCardInputBlockBase block={block} renderRight={renderRight}>
            <MBCardInputBlockDateContent
              block={block}
              // @ts-expect-error -- strict migration
              pickerMode={pickerMode}
              onOpenPicker={handleOpenPicker}
              onClosePicker={handleClosePicker}
            />
          </MBCardInputBlockBase>
        </View>
      </TouchableOpacity>
      {!!pickerMode && (
        <MBCardInputBlockDatePicker
          mode={pickerMode}
          block={block}
          anchorName={anchorName}
          onChange={onChange}
          onClose={handleClosePicker}
        />
      )}
    </>
  );
}
